// ═══════════════════════════════════════════════════════════════
// useChecklistItems — manages checklist items for a single invoice
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { fetchRecords, createRecord, updateRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from '../api/fields';
import type { ChecklistItem, ChecklistItemStatus, InvoiceType } from '../types/checklist';

const FCI = FIELDS.debrisChecklistItems;

function mapItem(r: Record<string, unknown>): ChecklistItem {
  const rid = Number((r as any).record_id) || Number((r as any).id) || 0;
  return {
    id: rid,
    invoiceId: String(r[FCI.invoiceId] ?? ''),
    invoiceType: String(r[FCI.invoiceType] ?? '') as InvoiceType,
    templateItemId: String(r[FCI.templateItemId] ?? ''),
    label: String(r[FCI.label] ?? ''),
    evidenceType: (String(r[FCI.evidenceType] ?? '') || 'manual') as ChecklistItem['evidenceType'],
    status: (String(r[FCI.status] ?? '') || 'pending') as ChecklistItemStatus,
    fileDocId: String(r[FCI.fileDocId] ?? '') || undefined,
    fileName: String(r[FCI.fileName] ?? '') || undefined,
    waiverReason: String(r[FCI.waiverReason] ?? '') || undefined,
    checkedBy: String(r[FCI.checkedBy] ?? '') || undefined,
    checkedAt: String(r[FCI.checkedAt] ?? '') || undefined,
    notes: String(r[FCI.notes] ?? '') || undefined,
  };
}

export function useChecklistItems(invoiceId: string | null, invoiceType: InvoiceType | null) {
  const [items, setItems] = useState<ChecklistItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!invoiceId) return;
    setLoading(true);
    try {
      const res = await fetchRecords<Record<string, unknown>>(TABLES.debrisChecklistItems, { limit: 50 });
      setItems(res.records.map(mapItem).filter(i => i.invoiceId === invoiceId));
    } catch { /* ignore */ }
    setLoading(false);
  }, [invoiceId]);

  useEffect(() => { load(); }, [load]);

  const saveItem = useCallback(async (
    templateItemId: string,
    label: string,
    evidenceType: 'file' | 'manual',
    status: ChecklistItemStatus,
    extra?: {
      fileDocId?: string;
      fileName?: string;
      waiverReason?: string;
      notes?: string;
      checkedBy?: string;
    },
  ) => {
    if (!invoiceId || !invoiceType) return;
    const existing = items.find(i => i.templateItemId === templateItemId);
    const now = new Date().toISOString();
    const payload: Record<string, unknown> = {
      [FCI.invoiceId]: invoiceId,
      [FCI.invoiceType]: invoiceType,
      [FCI.templateItemId]: templateItemId,
      [FCI.label]: label,
      [FCI.evidenceType]: evidenceType,
      [FCI.status]: status,
      [FCI.checkedAt]: now,
      ...(extra?.fileDocId !== undefined ? { [FCI.fileDocId]: extra.fileDocId } : {}),
      ...(extra?.fileName !== undefined ? { [FCI.fileName]: extra.fileName } : {}),
      ...(extra?.waiverReason !== undefined ? { [FCI.waiverReason]: extra.waiverReason } : {}),
      ...(extra?.notes !== undefined ? { [FCI.notes]: extra.notes } : {}),
      ...(extra?.checkedBy !== undefined ? { [FCI.checkedBy]: extra.checkedBy } : {}),
    };
    if (existing) {
      await updateRecord(TABLES.debrisChecklistItems, existing.id, payload);
    } else {
      await createRecord(TABLES.debrisChecklistItems, payload);
    }
    await load();
  }, [invoiceId, invoiceType, items, load]);

  return { items, loading, load, saveItem };
}
